/**
 * UpdatePlacementModal - Coller des lignes depuis le Drive pour mettre à jour BLOCK/RANG/SIEGE
 * Match par email_compte + evenement + categorie + source — priorité REF DE VENTE
 * Met à jour TOUS les ACHAT (attribués + non-attribués + validés).
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { MapPin, Loader2, X, Check, Eye, Play, AlertTriangle, ClipboardPaste } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = '';

const STATUS_CONFIG = {
  matched:     { label: 'Modifier',     bg: 'bg-emerald-900/15', text: 'text-emerald-400' },
  already_set: { label: 'Déjà rempli',  bg: 'bg-blue-900/15',    text: 'text-blue-400' },
  ambiguous:   { label: '⚠ AMBIGU',     bg: 'bg-amber-900/30',   text: 'text-amber-300' },
  not_found:   { label: 'Introuvable',  bg: 'bg-red-900/15',     text: 'text-red-400' },
  no_data:     { label: 'Sans empl.',   bg: 'bg-zinc-800/40',    text: 'text-zinc-500' },
};

export const UpdatePlacementModal = ({ isOpen, onClose, onSuccess, project = "WORLD CUP 2026" }) => {
  const [step, setStep] = useState('paste'); // paste | preview | result
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // all|matched|ambiguous|not_found|already_set|no_data

  const handlePreview = async () => {
    if (!text.trim()) {
      toast.error('Collez vos données du Drive');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/tickets/match-placement-from-paste`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text, project, mode: 'preview' })
      });
      const data = await res.json();
      if (data.success) {
        setPreviewData(data);
        setStep('preview');
        const msg = [];
        if (data.matched > 0) msg.push(`${data.matched} à modifier`);
        if (data.already_set > 0) msg.push(`${data.already_set} déjà rempli(s)`);
        if (data.ambiguous > 0) msg.push(`⚠ ${data.ambiguous} ambigu(s)`);
        if (data.not_found > 0) msg.push(`${data.not_found} non trouvé(s)`);
        if (data.no_data > 0) msg.push(`${data.no_data} sans emplacement`);
        toast.info(msg.join(' · '));
      } else {
        toast.error(data.message || 'Erreur');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/tickets/match-placement-from-paste`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text, project, mode: 'apply' })
      });
      const data = await res.json();
      if (data.success) {
        setPreviewData(data);
        setStep('result');
        toast.success(`${data.matched} ticket(s) mis à jour !`);
        if (onSuccess) onSuccess();
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep('paste');
    setText('');
    setPreviewData(null);
    setStatusFilter('all');
    onClose();
  };

  const lineCount = text.trim() ? Math.max(0, text.trim().split('\n').length - 1) : 0;

  const filteredResults = (previewData?.results || []).filter(r => statusFilter === 'all' || r.status === statusFilter);

  const hasAmbiguous = (previewData?.ambiguous || 0) > 0;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-zinc-900 border-[#C4A052]/30 text-white max-w-7xl max-h-[90vh] flex flex-col" data-testid="update-placement-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <MapPin className="h-5 w-5 text-[#C4A052]" />
            MAJ Emplacements
            {step === 'preview' && <span className="text-sm font-normal text-zinc-400 ml-2">— Aperçu</span>}
            {step === 'result' && <span className="text-sm font-normal text-zinc-400 ml-2">— Terminé</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-3 min-h-0">

          {/* STEP 1: PASTE */}
          {step === 'paste' && (
            <div className="space-y-3">
              <div className="bg-zinc-800 rounded-lg p-3 text-sm text-zinc-400 space-y-1">
                <p className="text-white font-medium flex items-center gap-2">
                  <ClipboardPaste className="h-4 w-4 text-[#C4A052]" />
                  Collez directement vos lignes depuis le Drive (avec l'en-tête)
                </p>
                <p>Colonnes détectées automatiquement : <strong className="text-[#C4A052]">Match · Catégorie · MAIL COMPTE / MAIL CLIENT · Source · Block (ou Tribune) · Rang (Row) · Place (Seat) · REF DE VENTE</strong></p>
                <p>Matching : par <strong>match + catégorie (par n°) + source</strong>, puis <strong className="text-amber-300">REF DE VENTE</strong> en priorité pour le bon ticket. ITIXX ou MYCOM/G.COM supportés.</p>
                <p className="text-green-400">Met à jour block · rang · place <strong>+ email_compte + mail_client</strong>. TOUS les ACHAT (attribués + validés). Aucun ticket créé ni supprimé.</p>
              </div>
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Collez ici les lignes copiées depuis Google Sheets (avec l'en-tête)..."
                className="bg-zinc-800 border-zinc-700 text-white min-h-[300px] font-mono text-xs"
                data-testid="paste-textarea"
              />
              <p className="text-xs text-zinc-500">{lineCount} ligne(s) de données détectée(s)</p>
            </div>
          )}

          {/* STEP 2: PREVIEW */}
          {step === 'preview' && previewData && (
            <div className="flex-1 flex flex-col gap-3 min-h-0">
              {/* Stats */}
              <div className="flex items-center gap-2 bg-zinc-800 rounded-lg p-3 text-sm flex-wrap">
                <span className="text-zinc-300 mr-2">{previewData.total_rows} ligne(s)</span>
                <FilterPill active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} className="bg-zinc-700 text-zinc-200">Tout</FilterPill>
                <FilterPill active={statusFilter === 'matched'} onClick={() => setStatusFilter('matched')} className="bg-emerald-700/40 text-emerald-300">{previewData.matched} à modifier</FilterPill>
                {previewData.ambiguous > 0 && (
                  <FilterPill active={statusFilter === 'ambiguous'} onClick={() => setStatusFilter('ambiguous')} className="bg-amber-700/50 text-amber-200 ring-1 ring-amber-400">⚠ {previewData.ambiguous} ambigu(s)</FilterPill>
                )}
                {previewData.already_set > 0 && (
                  <FilterPill active={statusFilter === 'already_set'} onClick={() => setStatusFilter('already_set')} className="bg-blue-700/40 text-blue-300">{previewData.already_set} déjà rempli</FilterPill>
                )}
                {previewData.not_found > 0 && (
                  <FilterPill active={statusFilter === 'not_found'} onClick={() => setStatusFilter('not_found')} className="bg-red-700/40 text-red-300">{previewData.not_found} introuvable</FilterPill>
                )}
                {previewData.no_data > 0 && (
                  <FilterPill active={statusFilter === 'no_data'} onClick={() => setStatusFilter('no_data')} className="bg-zinc-700/40 text-zinc-400">{previewData.no_data} sans empl.</FilterPill>
                )}
              </div>

              {hasAmbiguous && (
                <div className="bg-amber-900/30 border border-amber-500/50 rounded-lg px-3 py-2 text-sm text-amber-200 flex items-start gap-2" data-testid="ambiguous-warning">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong>Attention :</strong> {previewData.ambiguous} ligne(s) ambigüe(s). Vérifiez-les en filtrant sur "⚠ ambigu(s)" avant d'appliquer.
                  </div>
                </div>
              )}

              {/* Table complète */}
              <div className="overflow-auto flex-1 border border-zinc-700 rounded-lg min-h-0" data-testid="preview-table">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-800 sticky top-0 z-10">
                    <tr>
                      <th className="px-2 py-2 text-left text-zinc-400">#</th>
                      <th className="px-2 py-2 text-left text-zinc-400">Statut</th>
                      <th className="px-2 py-2 text-left text-zinc-400">Match</th>
                      <th className="px-2 py-2 text-left text-zinc-400">Cat.</th>
                      <th className="px-2 py-2 text-left text-zinc-400 border-l border-zinc-700">Email compte act.</th>
                      <th className="px-2 py-2 text-left text-zinc-400">Email compte ⇒</th>
                      <th className="px-2 py-2 text-left text-zinc-400 border-l border-zinc-700">Mail client act.</th>
                      <th className="px-2 py-2 text-left text-zinc-400">Mail client ⇒</th>
                      <th className="px-2 py-2 text-left text-zinc-400 border-l border-zinc-700">Source</th>
                      <th className="px-2 py-2 text-left text-zinc-400">Ref Vente</th>
                      <th className="px-2 py-2 text-left text-zinc-400 border-l border-zinc-700">Block act.</th>
                      <th className="px-2 py-2 text-left text-zinc-400">Rang act.</th>
                      <th className="px-2 py-2 text-left text-zinc-400">Place act.</th>
                      <th className="px-2 py-2 text-left text-zinc-400 border-l border-zinc-700">Block ⇒</th>
                      <th className="px-2 py-2 text-left text-zinc-400">Rang ⇒</th>
                      <th className="px-2 py-2 text-left text-zinc-400">Place ⇒</th>
                      <th className="px-2 py-2 text-left text-zinc-400">Info</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredResults.map((r, i) => {
                      const cfg = STATUS_CONFIG[r.status] || { label: r.status, bg: '', text: 'text-zinc-400' };
                      const isAmb = r.status === 'ambiguous';
                      const emailChanges = r.new_email_compte && r.current_email_compte && r.new_email_compte.toLowerCase() !== r.current_email_compte.toLowerCase();
                      const emailNew = r.new_email_compte && !r.current_email_compte;
                      const mcChanges = r.new_mail_client && r.current_mail_client && r.new_mail_client.toLowerCase() !== r.current_mail_client.toLowerCase();
                      const mcNew = r.new_mail_client && !r.current_mail_client;
                      return (
                        <tr key={i} className={`border-t border-zinc-800 ${cfg.bg} ${isAmb ? 'ring-1 ring-amber-500/40' : ''}`}>
                          <td className="px-2 py-1.5 text-zinc-500 font-mono">{r.line_no || i + 1}</td>
                          <td className="px-2 py-1.5">
                            <span className={`${cfg.text} font-medium whitespace-nowrap`}>{cfg.label}</span>
                          </td>
                          <td className="px-2 py-1.5 text-zinc-300 truncate max-w-[180px]" title={r.evenement}>{r.evenement || ''}</td>
                          <td className="px-2 py-1.5 text-zinc-400">{r.categorie || ''}</td>
                          <td className="px-2 py-1.5 text-zinc-500 truncate max-w-[160px] border-l border-zinc-800" title={r.current_email_compte}>{r.current_email_compte || <span className="text-zinc-700">—</span>}</td>
                          <td className={`px-2 py-1.5 truncate max-w-[180px] ${emailChanges || emailNew ? 'text-[#C4A052] font-bold' : 'text-zinc-500'}`} title={r.new_email_compte}>
                            {r.new_email_compte || <span className="text-zinc-700">—</span>}
                          </td>
                          <td className="px-2 py-1.5 text-zinc-500 truncate max-w-[160px] border-l border-zinc-800" title={r.current_mail_client}>{r.current_mail_client || <span className="text-zinc-700">—</span>}</td>
                          <td className={`px-2 py-1.5 truncate max-w-[180px] ${mcChanges || mcNew ? 'text-[#C4A052] font-bold' : 'text-zinc-500'}`} title={r.new_mail_client}>
                            {r.new_mail_client || <span className="text-zinc-700">—</span>}
                          </td>
                          <td className="px-2 py-1.5 text-zinc-400 border-l border-zinc-800">{r.source || ''}</td>
                          <td className="px-2 py-1.5 text-amber-300 font-mono">{r.ref_vente || <span className="text-zinc-600">—</span>}</td>
                          <td className="px-2 py-1.5 text-zinc-500 border-l border-zinc-800">{r.current_block || <span className="text-zinc-700">—</span>}</td>
                          <td className="px-2 py-1.5 text-zinc-500">{r.current_rang || <span className="text-zinc-700">—</span>}</td>
                          <td className="px-2 py-1.5 text-zinc-500">{r.current_place || <span className="text-zinc-700">—</span>}</td>
                          <td className="px-2 py-1.5 border-l border-zinc-800">
                            {r.new_block ? <span className="text-[#C4A052] font-bold">{r.new_block}</span> : <span className="text-zinc-700">—</span>}
                          </td>
                          <td className="px-2 py-1.5">
                            {r.new_rang ? <span className="text-[#C4A052] font-bold">{r.new_rang}</span> : <span className="text-zinc-700">—</span>}
                          </td>
                          <td className="px-2 py-1.5">
                            {r.new_place ? <span className="text-[#C4A052] font-bold">{r.new_place}</span> : <span className="text-zinc-700">—</span>}
                          </td>
                          <td className="px-2 py-1.5 text-xs text-zinc-500 truncate max-w-[260px]" title={r.info}>{r.info || ''}</td>
                        </tr>
                      );
                    })}
                    {filteredResults.length === 0 && (
                      <tr>
                        <td colSpan={17} className="text-center py-6 text-zinc-500">Aucune ligne dans ce filtre</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* STEP 3: RESULT */}
          {step === 'result' && previewData && (
            <div className="bg-zinc-800 rounded-lg p-6 text-center space-y-3">
              <Check className="h-12 w-12 text-green-400 mx-auto" />
              <p className="text-lg text-green-400 font-medium">{previewData.matched} ticket(s) mis à jour</p>
              <div className="text-sm text-zinc-400 space-y-1">
                {previewData.already_set > 0 && <p>{previewData.already_set} déjà rempli(s) (ignorés)</p>}
                {previewData.ambiguous > 0 && <p className="text-amber-300">⚠ {previewData.ambiguous} ambigu(s) — non appliqué(s)</p>}
                {previewData.not_found > 0 && <p className="text-red-400">{previewData.not_found} non trouvé(s)</p>}
              </div>
              <p className="text-xs text-zinc-500 mt-4">Aucun ticket créé ni supprimé.</p>
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2 pt-2">
          {step === 'preview' && (
            <Button variant="outline" onClick={() => setStep('paste')} className="border-zinc-700" data-testid="back-btn">
              Retour
            </Button>
          )}
          <Button variant="outline" onClick={handleClose} className="border-zinc-700">
            <X className="h-4 w-4 mr-2" /> Fermer
          </Button>
          {step === 'paste' && (
            <Button onClick={handlePreview} disabled={loading || !text.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="preview-placement-btn">
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyse...</> : <><Eye className="h-4 w-4 mr-2" /> Aperçu</>}
            </Button>
          )}
          {step === 'preview' && previewData?.matched > 0 && (
            <Button onClick={handleApply} disabled={loading} className="bg-green-600 hover:bg-green-700" data-testid="apply-placement-btn">
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Application...</> : <><Play className="h-4 w-4 mr-2" /> Appliquer ({previewData.matched})</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const FilterPill = ({ active, onClick, className, children }) => (
  <button
    onClick={onClick}
    className={`px-2.5 py-1 rounded text-xs font-medium transition-all ${className || ''} ${active ? 'ring-2 ring-[#C4A052] ring-offset-1 ring-offset-zinc-800' : 'opacity-70 hover:opacity-100'}`}
  >
    {children}
  </button>
);

export default UpdatePlacementModal;
